import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/hooks/use-language';
import { Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface HighDemandEvent {
  id: string;
  name: string;
  occupancy: number;
  waitlist: number;
}

interface EventOccupancySummaryProps {
  totalEvents: number;
  totalSlots: number;
  totalRegistrations: number;
  waitlistCount: number;
  fullEvents: number;
  highDemandEvents: HighDemandEvent[];
  onEditEvent?: (eventId: string) => void;
}

export function EventOccupancySummary({
  totalEvents,
  totalSlots,
  totalRegistrations,
  waitlistCount,
  fullEvents,
  highDemandEvents,
  onEditEvent,
}: EventOccupancySummaryProps) {
  const { t } = useLanguage();
  const occupancyPercent = totalSlots > 0 ? Math.round((totalRegistrations / totalSlots) * 100) : 0;

  return (
    <Card className="w-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold flex items-center justify-between">
          <span>{t('admin.event_occupancy')}</span>
          <Badge variant={occupancyPercent >= 85 ? 'destructive' : 'secondary'} className="text-xs">
            {occupancyPercent}%
          </Badge>
        </CardTitle>
        <CardDescription className="text-xs text-muted-foreground flex flex-col sm:flex-row sm:flex-wrap gap-1 sm:gap-2">
          <span>{totalRegistrations}/{totalSlots} {t('admin.event_occupancy.slots_filled')}</span>
          <span className="hidden sm:inline">•</span>
          <span>{waitlistCount} {t('admin.event_occupancy.on_waitlist')}</span>
          <span className="hidden sm:inline">•</span>
          <span>{fullEvents}/{totalEvents} {t('admin.event_occupancy.full_events')}</span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Progress value={occupancyPercent} className="h-3" />
        {highDemandEvents.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {t('admin.event_occupancy.high_demand')}
            </p>
            <div className="space-y-2">
              {highDemandEvents.map((event) => (
                <div key={event.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 py-2 border-b last:border-0">
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="font-medium text-foreground truncate">{event.name}</span>
                    <span className="text-xs text-muted-foreground">
                      {event.occupancy}% {t('admin.event_occupancy.filled')}
                      {event.waitlist > 0 && ` • ${event.waitlist} ${t('admin.event_occupancy.waiting')}`}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant="outline" className="text-xs">
                      {event.waitlist > 0 ? t('admin.event_occupancy.waitlist_badge') : t('admin.event_occupancy.nearly_full')}
                    </Badge>
                    {onEditEvent && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEditEvent(event.id)}
                        className="h-8 w-8 p-0"
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
